import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useSite } from "@/context/SiteContext";
import { telHref, waHref } from "@/lib/api";
import { IMG } from "@/lib/content";
import { PageHero, SectionTitle } from "@/components/kit";
import { DynamicForm } from "@/components/forms/DynamicForm";
import { CONTACT_FIELDS } from "@/components/forms/configs";

function ContactCard({ name, logo, c, color, testid }) {
  if (!c) return null;
  return (
    <div className="card overflow-hidden" data-testid={testid}>
      <div className="h-1.5" style={{ background: color }} />
      <div className="p-6">
        <div className="flex items-center gap-3">
          <img src={logo} alt="" loading="lazy" className="h-12 w-12 object-contain" />
          <h3 className="font-display text-xl font-bold uppercase" style={{ color }}>{name}</h3>
        </div>
        <p className="mt-4 flex gap-2 text-neutral-700"><MapPin className="h-5 w-5 shrink-0" aria-hidden="true" />{c.address}</p>
        <a href={`mailto:${c.email}`} className="mt-2 flex gap-2 text-neutral-700 hover:underline"><Mail className="h-5 w-5" aria-hidden="true" />{c.email}</a>
        <div className="mt-5 space-y-2">
          {c.phones?.map((p, i) => (
            <div key={p} className="flex flex-wrap items-center gap-2">
              <span className="min-w-[150px] font-semibold text-ink">{p}</span>
              <a href={telHref(p)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-white" style={{ background: color }} data-testid={`${testid}-call-${i}`}>
                <Phone className="h-4 w-4" /> Appeler
              </a>
              <a href={waHref(p)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-sm font-semibold text-white" data-testid={`${testid}-wa-${i}`}>
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Contact() {
  useSeo("Contact", "Contactez l'ONG MA LEBA et l'ASSOCIATION MA LEBA : formulaire, téléphone, WhatsApp, e-mail.");
  const { settings } = useSite();
  const wa = settings?.whatsapp || "+33769017322";
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Nous écrire" title="Contact" intro="Chaque message ouvre une conversation : nous vous répondons par e-mail avec un lien personnel pour poursuivre l'échange." />
      <section className="container-x grid gap-10 py-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionTitle eyebrow="Formulaire" title="Envoyez-nous un message" className="mb-8" />
          <div className="card p-6 sm:p-8">
            <DynamicForm fields={CONTACT_FIELDS} endpoint="/contact" testid="contact-form" submitLabel="Envoyer le message"
              successText="Votre message a bien été reçu. Un accusé de réception vous a été envoyé par e-mail avec un lien pour suivre la conversation."
              renderSuccess={(d) => (
                <Link to={`/conversation/${d.token}`} className="btn-u mt-6" data-testid="contact-success-thread-link">Voir ma conversation</Link>
              )} />
          </div>
        </div>
        <aside className="space-y-6 lg:col-span-5">
          <div className="flex flex-wrap gap-3">
            <a href={telHref(wa)} className="btn-u flex-1" data-testid="contact-call-main"><Phone className="h-5 w-5" /> Appeler</a>
            <a href={waHref(wa, "Bonjour MA LEBA, ")} target="_blank" rel="noopener noreferrer" className="btn flex-1 bg-[#25D366] text-white hover:bg-[#1eb556]" data-testid="contact-whatsapp-main">
              <MessageCircle className="h-5 w-5" /> Écrire sur WhatsApp
            </a>
          </div>
          <ContactCard name="ONG MA LEBA" logo={IMG.logoOng} c={settings?.contacts?.ong} color="#1E7B34" testid="contact-card-ong" />
          <ContactCard name="Association MA LEBA" logo={IMG.logoAsso} c={settings?.contacts?.association} color="#C8161D" testid="contact-card-association" />
          <div className="card overflow-hidden">
            <iframe title="Carte de la zone de Brazzaville et Makana" loading="lazy" className="h-72 w-full" data-testid="contact-map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=14.95%2C-4.45%2C15.45%2C-4.05&layer=mapnik" />
            <p className="p-3 text-xs text-neutral-500">Zone de Makana / Brazzaville, République du Congo.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
